"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  ArrowUpRight,
  Building2,
  UtensilsCrossed,
  ChevronDown,
  Menu,
  Search,
  Sparkles,
  Store,
  X,
} from "lucide-react";
import Logo from "@/components/Logo";
import { useRegisterTypeModal } from "@/components/auth/RegisterTypeModalProvider";
import styles from "./header.module.css";

const navigation = [
  { label: "Como funciona", href: "/como-funciona" },
  { label: "Planos", href: "/planos" },
  { label: "Ajuda", href: "/ajuda" },
];
const solutions = [
  { label: "Food & Delivery", href: "/#food-delivery", icon: UtensilsCrossed },
  { label: "Comércio & Varejo", href: "/#comercio-varejo", icon: Store },
  { label: "Imobiliário", href: "/#imobiliario", icon: Building2 },
  {
    label: "Inteligência Artificial",
    href: "/#inteligencia-artificial",
    icon: Sparkles,
  },
];

export default function Header() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const solutionsRef = useRef<HTMLButtonElement>(null);
  const mobileRef = useRef<HTMLButtonElement>(null);
  const { openRegisterTypeModal } = useRegisterTypeModal();

  function closeMenus() {
    setMobileOpen(false);
    setSolutionsOpen(false);
  }

  useEffect(() => {
    function handlePointer(event: PointerEvent) {
      if (
        event.target instanceof Node &&
        !headerRef.current?.contains(event.target)
      ) {
        setMobileOpen(false);
        setSolutionsOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      if (solutionsOpen) {
        setSolutionsOpen(false);
        solutionsRef.current?.focus();
      } else if (mobileOpen) {
        setMobileOpen(false);
        mobileRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [mobileOpen, solutionsOpen]);

  useEffect(() => {
    const desktop = window.matchMedia("(min-width: 1366px)");
    function onResize() {
      setMobileOpen(false);
      setSolutionsOpen(false);
    }
    desktop.addEventListener("change", onResize);
    return () => desktop.removeEventListener("change", onResize);
  }, []);

  return (
    <header
      ref={headerRef}
      className={styles.header}
      onBlur={(event) => {
        if (
          event.relatedTarget &&
          !event.currentTarget.contains(event.relatedTarget)
        )
          closeMenus();
      }}
      onClick={(event) => {
        if (event.target instanceof Element && event.target.closest("a"))
          closeMenus();
      }}
    >
      <div className={styles.inner}>
        <Logo compact tagline="Tecnologia para Negócios" />
        <nav
          id="public-navigation"
          className={styles.navigation}
          data-open={mobileOpen}
          aria-label="Navegação principal"
        >
          <ul className={styles.links}>
            <li className={styles.solutions}>
              <button
                ref={solutionsRef}
                type="button"
                className={styles.solutionTrigger}
                aria-expanded={solutionsOpen}
                aria-controls="public-solutions"
                onClick={() => setSolutionsOpen((open) => !open)}
              >
                Soluções{" "}
                <ChevronDown
                  size={15}
                  className={solutionsOpen ? styles.rotated : undefined}
                />
              </button>
              <ul
                id="public-solutions"
                className={styles.solutionsList}
                hidden={!solutionsOpen}
              >
                {solutions.map(({ label, href, icon: Icon }) => (
                  <li key={href}>
                    <Link href={href}>
                      <Icon size={20} />
                      <span>{label}</span>
                      <ArrowUpRight size={14} />
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
            {navigation.map(({ label, href }) => (
              <li key={href}>
                <Link
                  href={href}
                  aria-current={pathname === href ? "page" : undefined}
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          <div className={styles.discoveryNav}>
            <Link
              href="/fornecedores"
              className={styles.companiesLink}
              aria-current={
                pathname === "/fornecedores"
                  ? "page"
                  : undefined
              }
            >
              <Search size={18} aria-hidden="true" />
              Encontrar empresas
            </Link>

            <Link
              href="/marketplace"
              className={styles.marketplace}
              aria-current={
                pathname === "/marketplace"
                  ? "page"
                  : undefined
              }
            >
              <Store size={18} aria-hidden="true" />
              Marketplace
            </Link>
          </div>

          <div className={styles.actions}>
            <Link href="/login" className={styles.login}>
              Entrar
            </Link>
            <button
              type="button"
              className={styles.register}
              onClick={() => {
                closeMenus();
                openRegisterTypeModal();
              }}
            >
              Começar agora <ArrowUpRight size={16} />
            </button>
          </div>
        </nav>
        <button
          ref={mobileRef}
          type="button"
          className={styles.mobileToggle}
          aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          aria-expanded={mobileOpen}
          aria-controls="public-navigation"
          onClick={() => {
            setMobileOpen((open) => !open);
            setSolutionsOpen(false);
          }}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>
  );
}
